import React from 'react';
import { Box, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { EditType, ExtraAgentData } from '../types';

const IMPLEMENTATION_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
  'Client Specific': { bg: '#F1FFEE', color: '#27742A', icon: '/images/client_specific.svg' },
  Embedded: { bg: '#FFF7F3', color: '#9A5A37', icon: '/images/embedded_icon.svg' },
  Extended: { bg: '#FFF7DF', color: '#B18600', icon: '/images/extended_icon.svg' },
};

interface InfoBoxProps {
  title: string;
  titleColor: string;
  icon: string;
  text: string;
  bg: string;
  extraSx?: object;
}

const InfoBox: React.FC<InfoBoxProps> = ({ title, titleColor, icon, text, bg, extraSx = {} }) => (
  <Box
    sx={{
      background: bg,
      borderRadius: '6px',
      padding: '12px',
      mt: '18px',
      width: { xs: '100%', sm: '50%' },
      minHeight: '126px',
      ...extraSx,
    }}
  >
    <Box sx={{ display: 'flex' }}>
      <img src={icon} alt="icon" />
      <Typography sx={{ fontWeight: 500, fontSize: '15px', color: titleColor, ml: '8px' }}>
        {title}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '15px', fontWeight: 400, mt: '10px' }}>{text}</Typography>
  </Box>
);

interface AgentsViewProps {
  llmAgents: any[];
  agentEmbeddedArray: any[];
  agentLlmResponse: ExtraAgentData[];
  isLoadingAgentLlmResponse: boolean;
  isEditing: boolean;
  onEdit: (type: EditType, item?: any) => void;
}

const AgentsView: React.FC<AgentsViewProps> = ({
  llmAgents,
  agentEmbeddedArray,
  agentLlmResponse,
  isLoadingAgentLlmResponse,
  isEditing,
  onEdit,
}) => (
  <>
    {llmAgents?.map((data: any, index: number) => {
      // Resolve which classification record matches this agent
      let matched: any = undefined;

      if (Array.isArray(agentEmbeddedArray)) {
        matched = agentEmbeddedArray.find((item: any) => item?.agent_name === data.agent_name);
      } else if (agentLlmResponse && typeof agentLlmResponse === 'object') {
        const candidateArray = Array.isArray((agentLlmResponse as any).agent_classification)
          ? (agentLlmResponse as any).agent_classification
          : Object.values(agentLlmResponse as any);
        if (Array.isArray(candidateArray)) {
          matched = candidateArray.find((item: any) => item?.agent_name === data.agent_name);
        }
      }

      const styleConfig = matched && IMPLEMENTATION_STYLES[matched?.implementation_type];

      return (
        <Grid key={index}>
          <Card sx={{ mb: 3, boxShadow: 'none' }}>
            <CardContent
              sx={{ border: '1px solid #DDE1E6', borderRadius: '10px', boxShadow: 'none' }}
            >
              {/* Agent header */}
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flex: 1, mr: 3 }}>
                  <img src="/images/agentIcon.svg" alt="agentIcon" />
                  <Box sx={{ ml: '12px' }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, fontSize: '12px', color: '#1B54F8' }}
                    >
                      {data?.agent_enabler?.join(', ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                        {data?.agent_name}
                      </Typography>
                      {isEditing && (
                        <DriveFileRenameOutlineIcon
                          sx={{
                            cursor: 'pointer',
                            height: '16px',
                            width: '16px',
                            color: 'grey.500',
                          }}
                          onClick={() => onEdit('agent_name', { index, name: data?.agent_name })}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Task + Implementation Type boxes */}
              <Box
                sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '15px' }}
              >
                <InfoBox
                  title="Task"
                  titleColor="#3286DB"
                  icon="/images/taskIcon.svg"
                  text={data?.agent_description}
                  bg="#F7FBFF"
                  extraSx={{
                    width:
                      matched || isLoadingAgentLlmResponse ? { xs: '100%', sm: '50%' } : '100%',
                  }}
                />

                {isLoadingAgentLlmResponse ? (
                  <Box
                    sx={{
                      borderRadius: '12px',
                      px: 2,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      background: '#FAFAFA',
                      gap: 1.5,
                      mt: '18px',
                      width: { xs: '100%', sm: '50%' },
                    }}
                  >
                    <Skeleton
                      variant="rounded"
                      width={90}
                      height={10}
                      animation="wave"
                      sx={{ borderRadius: '8px' }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={10}
                      width="100%"
                      animation="wave"
                      sx={{ borderRadius: '6px', mt: '10px' }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={10}
                      width="100%"
                      animation="wave"
                      sx={{ borderRadius: '6px' }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={10}
                      width="100%"
                      animation="wave"
                      sx={{ borderRadius: '6px' }}
                    />
                  </Box>
                ) : (
                  matched &&
                  styleConfig && (
                    <InfoBox
                      title={matched.implementation_type}
                      titleColor={styleConfig.color}
                      icon={styleConfig.icon}
                      text={matched.justification}
                      bg={styleConfig.bg}
                      extraSx={{ width: { xs: '100%', sm: '50%' } }}
                    />
                  )
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      );
    })}
  </>
);

export default AgentsView;
